import React from 'react';
import I18n from 'i18n-js';
import { debounce } from 'lodash';

import OffersNudge from '@reverbdotcom/commons/src/offers/offers_nudge';
import OffersCurrencyConversion from '@reverbdotcom/commons/src/offers/offers_currency_conversion';
import {
  core_apimessages_NegotiationParty as NegotiationParty,
  core_apimessages_NegotiationAction as NegotiationAction,
  reverb_config_cache_ExchangeRate as ExchangeRate,
  Listing,
  Negotiation,
  reverb_pricing_PriceRecommendation as PriceRecommendation,
} from '../../schemas/rql';
import FormGroupWithInput from '@reverbdotcom/commons/src/components/form_group_with_input';
import FormGroup from '@reverbdotcom/commons/src/components/form_group';
import * as NegotiationGetters from './negotiation_getters';
import { UserInputOffer } from '@reverbdotcom/commons/src/offers/negotiation_types';
import {
  CombinedSaleAndShippingNotice,
} from '@reverbdotcom/commons/src/offers/offer_notices';
import OfferAmountInputs from '@reverbdotcom/commons/src/offers/offer_amount_inputs';
import EstimatedValueAlert from '@reverbdotcom/commons/src/offers/estimated_value_alert';
import Currency from '@reverbdotcom/commons/src/currency';
import { ControlledBodyInput } from '../messages/body_input';
import { RCCheckbox, RCTag } from '@reverbdotcom/cadence/components';
import { MoneyInput } from '@reverbdotcom/commons/src/components/money_input';
import { parseAmount } from '@reverbdotcom/commons/src/money';
import { MParticleEventName, trackEvent } from '@reverbdotcom/commons/src/elog/mparticle_tracker';

export interface IProps {
  error?: string;
  listing: Listing;
  negotiation: Negotiation;
  currency: string;
  onOfferChange: (any) => void;
  offer: UserInputOffer;
  existingOffer?: UserInputOffer;
  party: NegotiationParty;
  action: NegotiationAction;
  exchangeRates: ExchangeRate[];
  useDesignChanges?: boolean;
  priceRecommendation?: PriceRecommendation;
}

function CounterNotices({ party, listing, negotiation }) {
  if (party === NegotiationParty.BUYER) {
    return (
      <p className="offers-form__notice offers-form__notice--purchase">
        {I18n.t(
          'discovery.offers.buyerCompletePurchaseNotice',
          { shopName: listing.shop.name },
        )}
      </p>
    );
  }

  return (
    <p className="offers-form__notice offers-form__notice--purchase">
      {I18n.t(
        'discovery.offers.sellerCompletePurchaseNotice',
        { buyerName: negotiation.buyer.name },
      )}
    </p>
  );
}

function CounterFields({ offer, listing, party, currency, negotiation, onOfferChange, useDesignChanges }) {
  const taxHint = negotiation.taxIncluded ? ` (${negotiation.taxIncludedHint})` : '';
  const currencyDescription = Currency.get(currency);
  const shippingPriceLabel = useDesignChanges ?
    `${I18n.t('discovery.offers.label.adjustCounterOfferShippingPrice')}${taxHint}` :
    `${I18n.t('discovery.offers.label.counterOfferShippingPrice')}${taxHint}`;
  const isSeller = party === NegotiationParty.SELLER;

  return (
    <>
      <OfferAmountInputs
        offer={offer}
        listing={listing}
        party={party}
        action={NegotiationAction.COUNTER}
        currency={currency}
        taxable={negotiation}
        onOfferChange={onOfferChange}
        autoFocus
        useDesignChanges={useDesignChanges}
      />

      {!useDesignChanges && !isSeller && (
        <OffersNudge
          offerAmount={offer.price}
          originalAmount={listing.price.amount}
          currency={currency}
        />
      )}

      {isSeller && (
        <>
          {useDesignChanges && (
            <div className="offers-form__shipping-price-row">
              <MoneyInput
                label={shippingPriceLabel}
                id="offer-shipping-price"
                name="offer-shipping-price"
                currencyCode={currency}
                value={offer.shippingPrice}
                onChange={(change) => {
                  onOfferChange({ type: 'setShippingPrice', value: change.target.value });
                }}
                tag="optional"
                helpText={negotiation.sellerShippingWarning}
              />
            </div>
          )}

          {!useDesignChanges && (
            <FormGroupWithInput
              label={`${I18n.t('discovery.offers.label.counterOfferShippingPrice')}${taxHint}`}
              fieldName="shippingPrice"
              inputName="offer-shipping-price"
              currencyPrefix={currencyDescription.symbol}
              value={offer.shippingPrice}
              updateField={(change) => {
                onOfferChange({ type: 'setShippingPrice', value: change.shippingPrice });
              }}
              disabled={false}
              numberOnly
              required
            />
          )}
        </>
      )}
    </>
  );
}

export function UpdateOfferFormFields({
  listing, negotiation, currency, onOfferChange, offer, action, party, exchangeRates, error = '',
  useDesignChanges = false, priceRecommendation = undefined, existingOffer = undefined,
}: IProps) {
  const [photos, setPhotos] = React.useState([]);
  const [showMessage, setShowMessage] = React.useState(false);
  const [debouncedOfferPrice, setDebouncedOfferPrice] = React.useState('');

  const debounceOfferPrice = React.useCallback(debounce((newOfferPrice) => {
    setDebouncedOfferPrice(newOfferPrice);
  }, 200), []);

  React.useEffect(() => {
    debounceOfferPrice(offer.price);
  }, [offer.price, debounceOfferPrice]);

  const parsedOfferPrice = offer.price && parseAmount(offer.price, currency);
  const parsedExistingOfferPrice = existingOffer.price && parseAmount(existingOffer.price, currency);
  const parsedDebouncedOfferPrice = debouncedOfferPrice && parseAmount(debouncedOfferPrice, currency);
  const lastOffer = NegotiationGetters.lastOffer(negotiation);
  const shippingPriceInShopCurrency = NegotiationGetters.offerOriginalShippingPriceAmount(lastOffer);
  const isSeller = party === NegotiationParty.SELLER;
  const isCounteringOffer = action === NegotiationAction.COUNTER;
  const isRejectingOffer = action === NegotiationAction.REJECT;
  const showEstimatedValueAlert = priceRecommendation && (isCounteringOffer || isRejectingOffer);

  React.useEffect(() => {
    // If there's no price guide data available or we're not in an offer context that would show it (i.e. when
    // accepting), then we'll only emit a single InteractedWithOfferFlow event on render, since there won't be any
    // negotiation guidance. If there is price guide data available we don't do the track event here, since the
    // EstimatedValueAlert component will handle it.
    let negotiationGuidance;
    if (priceRecommendation && action === NegotiationAction.ACCEPT) {
      // if we're accepting an offer, price guide data isn't shown even if it's available, so make this field blank
      negotiationGuidance = '';
    } else {
      negotiationGuidance = 'No Price Guide';
    }

    if (!showEstimatedValueAlert) {
      trackEvent({
        eventName: MParticleEventName.InteractedWithOfferFlow,
        role: party.toLowerCase(),
        negotiationAction: action.toLowerCase(),
        negotiationGuidance: negotiationGuidance,
        offerStatus: negotiation.state.toLowerCase(),
      });
    }
  }, []);

  let messageLabel;
  if (useDesignChanges) {
    if (isCounteringOffer) {
      messageLabel = I18n.t('discovery.offers.messageLabel');
    } else {
      if (isSeller) {
        messageLabel = I18n.t('discovery.offers.messageToBuyerCheckboxLabel');
      } else {
        messageLabel = I18n.t('discovery.offers.messageToSellerCheckboxLabel');
      }
    }
  } else {
    if (isSeller) {
      messageLabel = I18n.t('discovery.offers.sellerMessageLabel');
    } else {
      messageLabel = I18n.t('discovery.offers.buyerMessageLabel');
    }
  }

  return (
    <div className="offers-form__body">
      {error && (
        <p className="offers-form__error">{error}</p>
      )}

      <div className="offers-form__fields">
        {useDesignChanges && showEstimatedValueAlert && (
          <EstimatedValueAlert
            offerPrice={parsedDebouncedOfferPrice || parsedExistingOfferPrice} // initialize with existing offer price, then switch to the one that they put in
            recommendedLowPrice={priceRecommendation.priceLow}
            recommendedHighPrice={priceRecommendation.priceHigh}
            party={party}
            negotiationAction={action}
            negotiationState={negotiation.state}
          />
        )}

        {isCounteringOffer && (
          <CounterFields
            offer={offer}
            listing={listing}
            party={party}
            currency={currency}
            negotiation={negotiation}
            onOfferChange={onOfferChange}
            useDesignChanges={useDesignChanges}
          />
        )}

        {useDesignChanges && !isSeller && parsedOfferPrice && currency != listing.shop.currency && (
          <section className="offers-form__currency-difference-alert">
            <p>
              {I18n.t('discovery.offers.currencyMayVary')}
            </p>
          </section>
        )}
      </div>

      {!useDesignChanges && (
        <OffersCurrencyConversion
          offerAmount={offer.price}
          shippingPrice={shippingPriceInShopCurrency}
          rates={exchangeRates}
          userCurrency={currency}
          shopCurrency={listing.shop.currency}
        />
      )}

      {useDesignChanges && isCounteringOffer && (
        <div className="d-flex fx-align-start mtb-2">
          <RCCheckbox
            id="show-message"
            name="show-message"
            checked={showMessage}
            label={isSeller ? I18n.t('discovery.offers.messageToBuyerCheckboxLabel') : I18n.t('discovery.offers.messageToSellerCheckboxLabel')}
            onChange={() => { setShowMessage(!showMessage); }}
          />
          <div className="ml-half">
            <RCTag>
              {I18n.t('discovery.offers.optional')}
            </RCTag>
          </div>
        </div>
      )}

      {(showMessage || !useDesignChanges || !isCounteringOffer) && (
        <FormGroup
          label={messageLabel}
          tagOptional={!useDesignChanges || !isCounteringOffer}
        >
          <ControlledBodyInput
            message={offer.message}
            onChange={(message) => {
              onOfferChange({ type: 'setMessage', 'value': message });
            }}
            photos={photos}
            onPhotosChange={setPhotos}
            inputNamespace=""
            bodyInputName="message"
            productId={listing.id}
            isSeller={isSeller}
            allowPhotos={false}
            isRequired={false}
          />
        </FormGroup>
      )}

      {!useDesignChanges && (
        <div className="offers-form__notices">
          <CombinedSaleAndShippingNotice listing={listing} />
          {isCounteringOffer &&
            <CounterNotices
              party={party}
              listing={listing}
              negotiation={negotiation}
            />
          }
        </div>
      )}
    </div>
  );
}

export default UpdateOfferFormFields;
